import { useState } from 'react';
import { Switch } from 'antd';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';
import './agrupamentoPorData.css';

/**
 * Agrupar por data, como o Explorador de Arquivos do Windows (Arthur, 22/10/2026): Dashboard,
 * Compras e Remarcação no Mitryus. Ligado por padrão; cada grupo abre e fecha no cabeçalho.
 */
export type Periodo = 'hoje' | 'semana' | 'mes' | 'ano' | 'anterior';

const ORDEM: Periodo[] = ['hoje', 'semana', 'mes', 'ano', 'anterior'];

export const ROTULO_DO_PERIODO: Record<Periodo, string> = {
    hoje: 'Hoje',
    semana: 'Última semana',
    mes: 'Último mês',
    ano: 'Último ano',
    anterior: 'Anterior',
};

const DIA = 24 * 60 * 60 * 1000;
const inicioDoDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Pelos dias de calendário: hoje; até 7 dias atrás; até 30; até 365; antes disso. */
export function periodoDaData(data: string | number | Date, agora = new Date()): Periodo {
    const dias = Math.round((inicioDoDia(agora) - inicioDoDia(new Date(data))) / DIA);
    if (dias <= 0) return 'hoje';
    if (dias <= 7) return 'semana';
    if (dias <= 30) return 'mes';
    if (dias <= 365) return 'ano';
    return 'anterior';
}

export type EntradaPorData<T> =
    | { tipo: 'periodo'; periodo: Periodo; total: number; fechado: boolean }
    | { tipo: 'item'; item: T };

/**
 * A lista com o cabeçalho de cada período na frente dos itens dele. A ordem dentro do período é a
 * da lista; os períodos vão do mais novo para o mais antigo (`crescente`: ao contrário). Item com
 * `junto` (a cópia logo abaixo do original) fica no período do item de cima. Período fechado
 * mostra só o cabeçalho. `total` conta os itens do período.
 */
export function entradasPorData<T>(
    itens: T[],
    dataDe: (item: T) => string | number | Date,
    fechados: Set<Periodo>,
    { crescente = false, junto }: { crescente?: boolean; junto?: (item: T) => boolean } = {},
): EntradaPorData<T>[] {
    const agora = new Date();
    const porPeriodo = new Map<Periodo, T[]>();
    let atual: Periodo = 'hoje';
    for (const item of itens) {
        if (!junto?.(item)) atual = periodoDaData(dataDe(item), agora);
        porPeriodo.set(atual, [...(porPeriodo.get(atual) || []), item]);
    }
    const entradas: EntradaPorData<T>[] = [];
    for (const periodo of crescente ? [...ORDEM].reverse() : ORDEM) {
        const doPeriodo = porPeriodo.get(periodo);
        if (!doPeriodo) continue;
        const fechado = fechados.has(periodo);
        entradas.push({ tipo: 'periodo', periodo, total: doPeriodo.length, fechado });
        if (!fechado) entradas.push(...doPeriodo.map(item => ({ tipo: 'item' as const, item })));
    }
    return entradas;
}

/** Quantos itens aparecem (os dos períodos abertos): é o que a página e a rolagem contam. */
export const itensAbertos = <T,>(entradas: EntradaPorData<T>[]) => entradas.filter(e => e.tipo === 'item').length;

/**
 * Os itens de `de` até `ate` (contando só itens, não cabeçalhos), cada um com o cabeçalho do seu
 * período. Página que começa no meio de um período repete o cabeçalho; período fechado aparece na
 * página em que cai.
 */
export function fatiarPorData<T>(entradas: EntradaPorData<T>[], de: number, ate: number): EntradaPorData<T>[] {
    const total = itensAbertos(entradas);
    const fatia: EntradaPorData<T>[] = [];
    let n = 0;
    let cabecalho: EntradaPorData<T> | null = null;
    for (const e of entradas) {
        if (e.tipo === 'periodo') {
            cabecalho = e;
            if (e.fechado && n >= de && (n < ate || (n === total && ate >= total))) { fatia.push(e); cabecalho = null; }
            continue;
        }
        if (n >= de && n < ate) {
            if (cabecalho) { fatia.push(cabecalho); cabecalho = null; }
            fatia.push(e);
        }
        n++;
    }
    return fatia;
}

const ler = (chave: string) => { try { return localStorage.getItem(chave); } catch { return null; } };
const guardar = (chave: string, valor: string) => { try { localStorage.setItem(chave, valor); } catch { /* só não lembra */ } };

/** Ligado ou não (lembrado por lista neste navegador) e os períodos fechados (não lembrados). */
export function useAgrupamentoPorData(lista: string) {
    const chave = `rico:agrupar-por-data:${lista}`;
    const [agrupar, setAgrupar] = useState(() => ler(chave) !== '0');
    const [fechados, setFechados] = useState<Set<Periodo>>(new Set());
    return {
        agrupar,
        fechados,
        mudarAgrupar: (ligado: boolean) => { setAgrupar(ligado); guardar(chave, ligado ? '1' : '0'); },
        alternar: (periodo: Periodo) => setFechados(atual => {
            const novo = new Set(atual);
            if (novo.has(periodo)) novo.delete(periodo); else novo.add(periodo);
            return novo;
        }),
    };
}

export function ChaveAgruparPorData({ ligado, onChange }: { ligado: boolean; onChange: (ligado: boolean) => void }) {
    return (
        <label className="agrupar-data-chave">
            <Switch size="small" checked={ligado} onChange={onChange} />
            <span>Agrupar por data</span>
        </label>
    );
}

/** A faixa do período: "Hoje (3)", destacada; clicar fecha ou abre o grupo. */
export function CabecalhoDoPeriodo({ periodo, total, fechado, onAlternar }: {
    periodo: Periodo; total: number; fechado: boolean; onAlternar: (periodo: Periodo) => void;
}) {
    return (
        <button
            type="button"
            className="agrupar-data-cabecalho"
            aria-expanded={!fechado}
            onClick={() => onAlternar(periodo)}
        >
            {fechado ? <LuChevronRight aria-hidden="true" /> : <LuChevronDown aria-hidden="true" />}
            <span className="agrupar-data-rotulo">{ROTULO_DO_PERIODO[periodo]}</span>
            <span className="agrupar-data-total">({total})</span>
        </button>
    );
}
